import styled, { CSSObject } from '@emotion/styled';
import { CancelToken } from 'axios';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  getSupplierEmployeeDocumentList,
  selectSupplierEmployeeDocumentList,
  supplierEmployeeDocumentListActions
} from '../../store/entities/supplierEmployeeDocumentList';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTypography } from '../common/basic/QcsTypography';
import { CellDate } from '../common/grid/CellDate';
import { Grid } from '../common/grid/Grid';
import { getFirstLastNameObj, getLangNameObj } from '../../utils/format';
import { formatDate } from '../../utils/date';
import { ShowImageButton } from '../invitation/Detail/Tabs/ShowImageButton';
import { getMediaToken } from '../../utils/storage';
import { BASE_URL_WITH_VERSION } from '../../utils/api';
import { SupplierCertDto } from '@api/models/suppliers/SupplierCertDto';

const HeaderContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
  }),
);

export const SupplierEmployeeDocument: FC = () => {
  const { t, i18n } = useTranslation();
  const supplierEmployeeDocumentList = useAppSelector(selectSupplierEmployeeDocumentList);
  const dispatch = useAppDispatch();
  const { supplierEmployeeId } = useParams();
  const mediaToken = getMediaToken();

  useEffect(() => {
    dispatch(supplierEmployeeDocumentListActions.reload(true));
  }, [dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getSupplierEmployeeDocumentList(supplierEmployeeId!, cancelToken));
  };

  const handleRenderData = (item: SupplierCertDto) => {
    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item.certificate)}</QcsTableCell>
        <QcsTableCell>{item.certificateType ? getLangNameObj(i18n, item.certificateType) : '-'}</QcsTableCell>
        <QcsTableCell align="center">
          {!!item.documentUrl && (
            <ShowImageButton
              tooltipTitle={t('supplierEmployeeDocument.link')}
              documentUrl={`${BASE_URL_WITH_VERSION + item.documentUrl}?token=${mediaToken}`}
            />
          )}
        </QcsTableCell>
        <CellDate value={item.validUntil} />
        <QcsTableCell>
          {item.uploadedBy ? (
            <>
              {getFirstLastNameObj(item.uploadedBy)}
              {item.created && (
                <>
                  <br />
                  {formatDate(i18n, item.created)}
                </>
              )}
            </>
          ) : (
            '-'
          )}
        </QcsTableCell>
        <QcsTableCell>
          {item.approvedBy ? (
            <>
              {getFirstLastNameObj(item.approvedBy)}
              {item.approvedOn && (
                <>
                  <br />
                  {formatDate(i18n, item.approvedOn)}
                </>
              )}
            </>
          ) : (
            '-'
          )}
        </QcsTableCell>
      </>
    );
  };

  return (
    <div>
      <HeaderContainer>
        <QcsTypography variant="h5">{t('supplierEmployeeDocument.title')}</QcsTypography>
      </HeaderContainer>
      <Grid<SupplierCertDto>
        headers={[
          { captionStr: 'supplierEmployeeDocument.name' },
          { captionStr: 'supplierEmployeeDocument.type' },
          {
            captionStr: 'supplierEmployeeDocument.link',
            sx: { textAlign: 'center' },
          },
          {
            captionStr: 'supplierEmployeeDocument.validTo',
            orderName: 'validUntil',
          },
          {
            captionStr: 'supplierEmployeeDocument.uploadedBy',
          },
          {
            captionStr: 'supplierEmployeeDocument.approvedBy',
          },
        ]}
        data={supplierEmployeeDocumentList}
        gridActions={supplierEmployeeDocumentListActions}
        renderData={handleRenderData}
        getData={handleGetData}
        search={false}
      />
    </div>
  );
};
